'use strict';

Object.defineProperty(exports, '__esModule', { value: true });

var tslib = require('tslib');
var baseBus = require('./base-bus.js');
var payloadMismatchError = require('./payload-mismatch-error.js');

var logCss = 'background-color: dodgerblue; color: white';
var eventReplay = 'EVENT_REPLAY';
var EventBus = /** @class */ (function (_super) {
    tslib.__extends(EventBus, _super);
    function EventBus() {
        var _this = _super !== null && _super.apply(this, arguments) || this;
        _this._lastId = 0;
        return _this;
    }
    EventBus.prototype.subscribe = function (param1, param2, options) {
        if (typeof param2 !== 'function') {
            throw new Error('Callback function must be supplied as the second argument.');
        }
        if (typeof param1 === 'string') {
            var channel = param1;
            var callback = param2;
            return this.subscribeSingleChannel(channel, callback, options);
        }
        var channels = param1;
        var multiCallback = param2;
        return this.subscribeMultipleChannels(channels, multiCallback, options);
    };
    /**
     * Publish to channel with an optional payload triggering command handler.
     * @param channel - name of event channel to send payload on
     * @param payload - payload to be sent
     *
     * @throws {@link PayloadMismatchError}
     * This exception is thrown if payload does is not valid against registered schema.
     */
    EventBus.prototype.publish = function (channel, payload) {
        var schema = this.getSchema(channel);
        if (this.isPayloadInvalid(channel, payload)) {
            throw new payloadMismatchError.PayloadMismatchError(channel, schema, payload);
        }
        this.setLatest(channel, payload);
        if (!this.getHandlers(channel).length) {
            this.logMissingHandler(channel);
            return;
        }
        this.logPublishedMessage(channel, payload);
        return this.resolveHandlers(channel, payload);
    };
    EventBus.prototype.getNextId = function () {
        return this._lastId++;
    };
    EventBus.prototype.getHandlers = function (channel) {
        var _a, _b, _c;
        return Array.from((_c = (_b = (_a = this._subscriptions.get(channel)) === null || _a === void 0 ? void 0 : _a.handlers) === null || _b === void 0 ? void 0 : _b.entries()) !== null && _c !== void 0 ? _c : []);
    };
    EventBus.prototype.setHandler = function (channel, callback, handlerId, options) {
        if (!this._subscriptions.has(channel)) {
            this.initChannel(channel);
        }
        var subscription = this._subscriptions.get(channel);
        subscription.handlers.set(handlerId, callback);
        if (options) {
            subscription.handlerOptions.set(handlerId, options);
        }
    };
    EventBus.prototype.resolveHandlers = function (channel, payload) {
        var _this = this;
        return new Promise(function (resolve, reject) { return tslib.__awaiter(_this, void 0, void 0, function () {
            var e_1;
            var _this = this;
            return tslib.__generator(this, function (_a) {
                switch (_a.label) {
                    case 0:
                        _a.trys.push([0, 2, , 3]);
                        return [4 /*yield*/, Promise.all(this.getHandlers(channel).map(function (_a) {
                                var handlerId = _a[0], handler = _a[1];
                                var options = _this._subscriptions
                                    .get(channel)
                                    .handlerOptions.get(handlerId);
                                if ((options === null || options === void 0 ? void 0 : options.distinct) &&
                                    _this.isDuplicateValueEmitted(channel)) {
                                    return;
                                }
                                return handler({
                                    channel: channel,
                                    payload: payload,
                                });
                            }))];
                    case 1:
                        _a.sent();
                        resolve();
                        return [3 /*break*/, 3];
                    case 2:
                        e_1 = _a.sent();
                        reject(e_1);
                        return [3 /*break*/, 3];
                    case 3: return [2 /*return*/];
                }
            });
        }); });
    };
    EventBus.prototype.canReplay = function (channel) {
        return typeof this.getLatest(channel) !== 'undefined';
    };
    EventBus.prototype.replayHandler = function (channel, handler) {
        var payload = this.getLatest(channel);
        return new Promise(function (resolve) {
            handler({
                channel: channel,
                payload: payload,
            });
            resolve();
        });
    };
    EventBus.prototype.getSubscription = function (channel, handlerId) {
        var _this = this;
        return {
            unsubscribe: function () {
                _this._subscriptions.get(channel).handlers.delete(handlerId);
                _this._subscriptions
                    .get(channel)
                    .handlerOptions.delete(handlerId);
            },
        };
    };
    EventBus.prototype.subscribeHandler = function (channel, callback, options) {
        this.logRegisteredHandler(channel);
        var handlerId = this.getNextId();
        this.setHandler(channel, callback, handlerId, options);
        return this.getSubscription(channel, handlerId);
    };
    EventBus.prototype.subscribeSingleChannel = function (channel, callback, options) {
        var _this = this;
        if ((options === null || options === void 0 ? void 0 : options.replay) && this.canReplay(channel)) {
            this.logHandlerReplay(channel);
            Promise.resolve().then(function () {
                _this.replayHandler(channel, callback);
            });
        }
        return this.subscribeHandler(channel, callback, options);
    };
    EventBus.prototype.subscribeMultipleChannels = function (channels, callback, options) {
        var _this = this;
        var multiChannelCallback = function (event) {
            if (channels.some(function (channel) { return !_this.canReplay(channel); })) {
                return;
            }
            callback({
                channel: event.channel,
                payload: _this.getLatestMultiChannel(channels),
            });
        };
        if (options === null || options === void 0 ? void 0 : options.replay) {
            this.logMultiHandlerReplay(channels);
            Promise.resolve().then(function () {
                multiChannelCallback({
                    channel: eventReplay,
                    payload: undefined,
                });
            });
        }
        var subscriptions = channels.map(function (channel) {
            return _this.subscribeHandler(channel, multiChannelCallback, options);
        });
        return {
            unsubscribe: function () {
                subscriptions.forEach(function (subscription) {
                    subscription.unsubscribe();
                });
            },
        };
    };
    EventBus.prototype.initChannel = function (channel) {
        _super.prototype.initChannel.call(this, channel);
        this._subscriptions.get(channel).handlers = new Map();
        this._subscriptions.get(channel).handlerOptions = new Map();
    };
    EventBus.prototype.logRegisteredHandler = function (channel) {
        this.log('%cEvent bus: registered handler for channel', logCss, channel);
    };
    EventBus.prototype.logMissingHandler = function (channel) {
        this.log('%cEvent bus: handler is missing for channel', logCss, channel);
    };
    EventBus.prototype.logPublishedMessage = function (channel, payload) {
        this.log('%cEvent bus: publishing message for channel', logCss, channel);
        this.log('%cEvent bus: message payload', logCss, payload);
    };
    EventBus.prototype.logHandlerReplay = function (channel) {
        var payload = this.getLatest(channel);
        this.log('%cEvent bus: replay handler for channel', logCss, channel);
        this.log('%cEvent bus: replayed payload', logCss, payload);
    };
    EventBus.prototype.logMultiHandlerReplay = function (channels) {
        var payload = this.getLatestMultiChannel(channels);
        this.log('%cEvent bus: replay handler for channel', logCss, channels);
        this.log('%cEvent bus: replayed payload', logCss, payload);
    };
    EventBus.prototype.getLatestMultiChannel = function (channels) {
        var _this = this;
        return channels.map(function (channel) { return _this.getLatest(channel); });
    };
    return EventBus;
}(baseBus.BaseBus));

exports.EventBus = EventBus;
exports.eventReplay = eventReplay;
